import { ChevronDown } from "lucide-react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

export type DocumentPreviewSection = {
  title: string
  fields: { label: string; required?: boolean; value: string }[]
}

/**
 * Read-only, collapsible rendering of a document's own field values — ported
 * from `otk/wireframes/src/components/DocumentPreviewDialog.tsx`'s
 * `DocumentCard`, byte-identical markup/classNames, minus its `photos`
 * branch: `OtkDocumentDetail` only carries EDRTZ photo metadata
 * (`photoId`/`note`), never a URL, so that branch would have nothing to
 * render here.
 */
export function DocumentCard({
  sections,
  className,
}: {
  sections: DocumentPreviewSection[]
  className?: string
}) {
  return (
    <div
      className={cn(
        "h-full overflow-auto px-0 py-2 sm:px-0 sm:py-4",
        className
      )}
    >
      <div className="w-full space-y-8">
        {sections.map((section) => (
          <Collapsible
            className="group/collapsible overflow-hidden rounded-lg border bg-muted/30"
            defaultOpen
            key={section.title}
          >
            <CollapsibleTrigger asChild>
              <button className="group/trigger flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-muted/40">
                <h3 className="text-base font-semibold">{section.title}</h3>
                <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-hover/trigger:text-foreground group-data-[state=open]/collapsible:rotate-180" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-4">
                <dl className="grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-x-8 gap-y-4">
                  {section.fields
                    .filter((field) => field.required || field.value.trim())
                    .map((field) => {
                      const isEmpty = field.value.trim().length === 0

                      return (
                        <div className="min-w-0" key={field.label}>
                          <dt className="text-xs font-medium text-muted-foreground">
                            {field.label}
                          </dt>
                          <dd
                            className={`mt-1 text-sm break-words ${
                              isEmpty ? "text-muted-foreground" : ""
                            }`}
                          >
                            {isEmpty ? "-" : field.value}
                          </dd>
                        </div>
                      )
                    })}
                </dl>
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  )
}
